/**
 * forest_field.ts — WHERE THE WOODS ARE (pure, worker-safe, seed-derived).
 *
 * The flora update (2026-09-07): the wild used to scatter lone trees at
 * 2–3 m; this field paints FORESTS — clustered stands with their own species
 * mix per biome, trees from medium (7 m) through large (up to 22 m) to
 * gigantic (up to 52 m), avoiding the sea and its coast, lakes (client-side),
 * steep ground and every settlement's working landscape.
 *
 * MIRRORED at server/src/lib/forest_field.ts (headers and imports differ;
 * the math is byte-identical — the settlements law): the server casts extra
 * creature dens INTO the woods from the same field, the client plants the
 * trees and paints the maps from it. `verify:forest` runs both and compares.
 *
 * Field = smoothstep-thresholded two-octave value noise (900 m + 260 m
 * lattices) over a per-biome cover fraction, gated by height above sea, a
 * coast buffer, slope and settlement distance. HASH LAW: FNV-1a with the
 * xorshift finalizer — a raw FNV over lattice coordinates shows its lanes.
 */
import { terrainHeight, biomeAt, type Biome } from '@shared/terrain.js';
import { settlementSites } from '@shared/settlements.js';

/** Fraction of a biome's land the woods claim (the noise quantile the threshold sits at). */
export const FOREST_COVER: Record<Biome, number> = {
  moss_wetland: 0.40, fungal_basin: 0.42, crystal_forest: 0.36, rocky_highlands: 0.30, regolith_plains: 0.22, dune_desert: 0.05,
};

export type TreeClass = 'medium' | 'large' | 'giant';
/** Height bands (m) per class — a Meshy tree is ~1.9 m raw, the scale is targetH / rawH. */
export const TREE_HEIGHT: Record<TreeClass, [number, number]> = { medium: [7, 12], large: [12, 22], giant: [28, 52] };

/** The woods' species per biome: existing foliage keys for medium/large, one generated giant (self-heals to the largest existing species). */
export const FOREST_SPECIES: Record<Biome, { medium: string[]; large: string[]; giant: string }> = {
  regolith_plains: { medium: ['foliage:copperleaf_tree', 'foliage:umbrella_acacia_tree'], large: ['foliage:umbrella_acacia_tree'], giant: 'foliage:giant_plains_tree' },
  crystal_forest: { medium: ['foliage:prism_birch_tree', 'foliage:shard_palm_tree', 'foliage:crystal_tree'], large: ['foliage:chime_willow_tree', 'foliage:crystal_tree'], giant: 'foliage:giant_crystal_tree' },
  dune_desert: { medium: ['foliage:smoke_juniper_tree', 'foliage:mirage_acacia_tree'], large: ['foliage:dune_palm_tree'], giant: 'foliage:giant_dune_tree' },
  rocky_highlands: { medium: ['foliage:crag_birch_tree', 'foliage:highland_pine_tree'], large: ['foliage:storm_cedar_tree', 'foliage:highland_pine_tree'], giant: 'foliage:giant_highland_tree' },
  moss_wetland: { medium: ['foliage:paperbark_swamp_tree', 'foliage:lantern_willow_tree'], large: ['foliage:wetland_mangrove_tree', 'foliage:lantern_willow_tree'], giant: 'foliage:giant_wetland_tree' },
  fungal_basin: { medium: ['foliage:glowfern_tree', 'foliage:myco_lantern_tree', 'foliage:mushroom_tree'], large: ['foliage:weeping_lumen_tree', 'foliage:bracket_titan_tree'], giant: 'foliage:giant_fungal_tree' },
};

/** Metres above the sea a tree needs; the coast buffer samples this far around. */
export const FOREST_SEA_MARGIN_M = 6;
export const FOREST_COAST_R_M = 48;
/** Woods keep this far from a settlement's centre (its plan + hinterland), fading in over FOREST_SITE_FADE_M more. */
export const FOREST_SITE_KEEP_M = 420;
export const FOREST_SITE_FADE_M = 220;
/** Above this rise per metre the ground is a cliff, not a wood. */
export const FOREST_MAX_SLOPE = 0.85;

// ── hashing + noise ─────────────────────────────────────────────────────────
function fnv(seed: string, salt: string, a: number, b: number): number {
  let h = 0x811c9dc5;
  const s = `${seed}|${salt}`;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= a; h = Math.imul(h, 0x01000193);
  h ^= b; h = Math.imul(h, 0x01000193);
  // xorshift finalizer: FNV alone leaves lattice lanes
  h ^= h >>> 13; h = Math.imul(h, 0x5bd1e995); h ^= h >>> 15;
  return (h >>> 0) / 4294967296;
}
function smooth(t: number): number { return t * t * (3 - 2 * t); }
/** Bilinear value noise on a `wl`-metre lattice, [0,1). */
export function forestNoise(seed: string, salt: string, x: number, z: number, wl: number): number {
  const gx = Math.floor(x / wl), gz = Math.floor(z / wl);
  const fx = smooth(x / wl - gx), fz = smooth(z / wl - gz);
  const a = fnv(seed, salt, gx, gz), b = fnv(seed, salt, gx + 1, gz), c = fnv(seed, salt, gx, gz + 1), d = fnv(seed, salt, gx + 1, gz + 1);
  return (a * (1 - fx) + b * fx) * (1 - fz) + (c * (1 - fx) + d * fx) * fz;
}

// ── settlement distance (bucketed, cached per seed) ─────────────────────────
const SITE_CELL_M = 4096;
const siteGrid = new Map<string, Map<string, Array<{ x: number; z: number }>>>();
function siteBuckets(seed: string): Map<string, Array<{ x: number; z: number }>> {
  let g = siteGrid.get(seed);
  if (!g) {
    g = new Map();
    for (const s of settlementSites(seed)) {
      const k = `${Math.floor(s.centerX / SITE_CELL_M)},${Math.floor(s.centerZ / SITE_CELL_M)}`;
      const a = g.get(k) ?? []; a.push({ x: s.centerX, z: s.centerZ }); g.set(k, a);
    }
    siteGrid.set(seed, g);
  }
  return g;
}
/** Distance to the nearest settlement centre (capped at one bucket cell — far enough for the keep-out). */
export function nearestSiteDist(seed: string, x: number, z: number): number {
  const g = siteBuckets(seed);
  const cx = Math.floor(x / SITE_CELL_M), cz = Math.floor(z / SITE_CELL_M);
  let best = SITE_CELL_M;
  for (let i = -1; i <= 1; i++) for (let j = -1; j <= 1; j++) {
    const a = g.get(`${cx + i},${cz + j}`); if (!a) continue;
    for (const s of a) { const d = Math.hypot(s.x - x, s.z - z); if (d < best) best = d; }
  }
  return best;
}

// ── the noise's own quantiles ───────────────────────────────────────────────
// The two-octave sum is bell-shaped, not uniform: a threshold of 1 − cover claimed
// 2 % of the plains instead of 22 %. The threshold for a cover fraction is read
// off the combined noise's empirical CDF (seed-independent — the lattice values
// are uniform hashes, so the sum's distribution is the same for every seed).
let quantiles: Float64Array | null = null;
function noiseQuantile(pAbove: number): number {
  if (!quantiles) {
    const N = 4096; const v = new Float64Array(N);
    for (let i = 0; i < N; i++) { const x = (i * 7919.37) % 100000, z = (i * 104729.13) % 100000; v[i] = 0.62 * forestNoise('quantile', 'woodsA', x, z, 900) + 0.38 * forestNoise('quantile', 'woodsB', x, z, 260); }
    quantiles = v.sort();
  }
  const i = Math.min(quantiles.length - 1, Math.max(0, Math.round((1 - pAbove) * (quantiles.length - 1))));
  return quantiles[i];
}

/**
 * Forest density at (x, z): 0 = open land, 1 = deep woods. `sea` is the
 * Commons sea level (the caller's side computes it). Lakes are a client-side
 * bake and are applied by the planter, not here.
 */
export function forestDensity(seed: string, x: number, z: number, sea: number): number {
  const biome = biomeAt(seed, x, z);
  const cover = FOREST_COVER[biome] ?? 0;
  if (cover <= 0) return 0;
  const h = terrainHeight(seed, x, z);
  if (h < sea + FOREST_SEA_MARGIN_M) return 0;
  // coast buffer: any low sample around and the stand thins to nothing
  const r = FOREST_COAST_R_M;
  const hE = terrainHeight(seed, x + r, z), hW = terrainHeight(seed, x - r, z), hN = terrainHeight(seed, x, z + r), hS = terrainHeight(seed, x, z - r);
  if (Math.min(hE, hW, hN, hS) < sea + 3) return 0;
  // slope from the same four samples
  const slope = Math.max(Math.abs(hE - hW), Math.abs(hN - hS)) / (2 * r);
  if (slope > FOREST_MAX_SLOPE) return 0;
  const n = 0.62 * forestNoise(seed, 'woodsA', x, z, 900) + 0.38 * forestNoise(seed, 'woodsB', x, z, 260);
  const thr = noiseQuantile(cover);
  let d = n <= thr - 0.05 ? 0 : n >= thr + 0.09 ? 1 : smooth((n - (thr - 0.05)) / 0.14);
  if (d <= 0) return 0;
  const ds = nearestSiteDist(seed, x, z);
  if (ds < FOREST_SITE_KEEP_M) return 0;
  if (ds < FOREST_SITE_KEEP_M + FOREST_SITE_FADE_M) d *= (ds - FOREST_SITE_KEEP_M) / FOREST_SITE_FADE_M;
  return d;
}

/** Inside the woods proper (the map's forest mask, the creatures' dens). */
export function inWoods(seed: string, x: number, z: number, sea: number): boolean { return forestDensity(seed, x, z, sea) >= 0.5; }

// ── tree placement per chunk ────────────────────────────────────────────────
export interface ForestTree { x: number; z: number; key: string; cls: TreeClass; targetH: number; yaw: number; density: number }
/** Planting lattice (m): one candidate per cell, jittered — clustering comes from the density field. */
export const FOREST_CELL_M = 6.5;
/** Occupancy at full density: a 6.5 m lattice at 0.34 ≈ 1 tree per 124 m² (≈ 530 trees in a deep 256 m chunk).
 *  0.55 (855 trees) drew 123 M triangles at the potato tier — the mesh budget, not the ecology, sets this. */
export const FOREST_MAX_OCCUPANCY = 0.34;

/**
 * The woods' trees for a `size`-metre chunk whose min corner is (x0, z0).
 * Deterministic; keep-outs the planter owns (roads, pads, lakes, seat slope)
 * are applied by the caller.
 */
/**
 * Memo of planted chunks (LRU, client-side only — the server mirror plants
 * once per bake). WHY: the fauna's perch refresh asked for 4–9 chunks every
 * 2 s and the lattice (1,521 density samples × 5 terrain evaluations + a
 * 312-site scan each) cost 200–320 ms per refresh on the main thread — a
 * 12–19 frame stall every two seconds (2026-09-12). Callers must NOT mutate
 * the returned list.
 */
const TREES_MEMO = new Map<string, ForestTree[]>();
const TREES_MEMO_MAX = 128;
export function forestTreesForChunk(seed: string, x0: number, z0: number, size: number, sea: number, occupancyScale = 1): ForestTree[] {
  const memoKey = `${seed}|${x0}|${z0}|${size}|${sea}|${occupancyScale}`;
  const hit = TREES_MEMO.get(memoKey);
  if (hit) { TREES_MEMO.delete(memoKey); TREES_MEMO.set(memoKey, hit); return hit; } // LRU touch
  const out = plantChunk(seed, x0, z0, size, sea, occupancyScale);
  TREES_MEMO.set(memoKey, out);
  if (TREES_MEMO.size > TREES_MEMO_MAX) TREES_MEMO.delete(TREES_MEMO.keys().next().value as string);
  return out;
}
function plantChunk(seed: string, x0: number, z0: number, size: number, sea: number, occupancyScale: number): ForestTree[] {
  const out: ForestTree[] = [];
  const n = Math.max(1, Math.round(size / FOREST_CELL_M));
  const cell = size / n;
  const gx0 = Math.round(x0 / cell), gz0 = Math.round(z0 / cell);
  for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) {
    const gx = gx0 + i, gz = gz0 + j;
    const r0 = fnv(seed, 'tree0', gx, gz), r1 = fnv(seed, 'tree1', gx, gz), r2 = fnv(seed, 'tree2', gx, gz), r3 = fnv(seed, 'tree3', gx, gz);
    const x = x0 + (i + 0.15 + r0 * 0.7) * cell, z = z0 + (j + 0.15 + r1 * 0.7) * cell;
    const d = forestDensity(seed, x, z, sea);
    if (d <= 0.05) continue;
    if (r2 > Math.pow(d, 1.4) * FOREST_MAX_OCCUPANCY * occupancyScale) continue; // the tier thins the stand, never its outline
    const biome = biomeAt(seed, x, z);
    const sp = FOREST_SPECIES[biome]; if (!sp) continue;
    // size class: giants only deep in the stand and rare; large a third; the rest medium
    let cls: TreeClass = 'medium';
    if (d > 0.75 && r3 < 0.07) cls = 'giant'; else if (r3 < 0.07 + 0.30) cls = 'large';
    const pool = cls === 'giant' ? [sp.giant] : cls === 'large' ? sp.large : sp.medium;
    const key = pool[Math.floor(fnv(seed, 'tree4', gx, gz) * pool.length) % pool.length];
    const [h0, h1] = TREE_HEIGHT[cls];
    const targetH = h0 + fnv(seed, 'tree5', gx, gz) * (h1 - h0);
    out.push({ x, z, key, cls, targetH, yaw: fnv(seed, 'tree6', gx, gz) * Math.PI * 2, density: d });
  }
  return out;
}

/** The biome's largest existing species — the giant's stand-in until its key is registered. */
export function giantFallback(biome: Biome): string { return FOREST_SPECIES[biome].large[0]; }
